import { Component, signal, OnInit, ChangeDetectionStrategy } from '@angular/core';

import { ActivatedRoute, Router } from '@angular/router';
import { ToolbarComponent } from '../../core/layout/components/toolbar/toolbar.component';
import { LegalEntityDetailComponent } from './components/legal-entity-detail/legal-entity-detail.component';
import { FormsModule } from '@angular/forms';
import { AzSearchbarComponent } from '../../shared/components/az-searchbar/az-searchbar.component';
import {
  LegalEntityService,
  CountryGroup as ServiceCountryGroup,
  BcrStatusDto,
} from './services/legal-entity.service';
type CountryGroup = ServiceCountryGroup;

import { SpinnerComponent } from '../../shared/components/spinner/spinner.component';
import { SelectedLegalEntityService } from '../../shared/services/selected-legal-entity.service';
import { AccordionPanelComponent } from '../../shared/components/accordion-panel/accordion-panel.component';
import { ScrollActivityDirective } from '../../shared/directives/scroll-activity.directive';
import { UserPermissionsService } from '../../core/auth/services/user-permissions.service';

@Component({
  selector: 'app-legal-entity-list',
  imports: [
    FormsModule,
    ToolbarComponent,
    LegalEntityDetailComponent,
    AzSearchbarComponent,
    SpinnerComponent,
    AccordionPanelComponent,
    ScrollActivityDirective,
  ],
  templateUrl: './legal-entity-list.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./legal-entity-list.component.scss'],
})
export class LegalEntityListComponent implements OnInit {
  private countryGroups: CountryGroup[] = [];
  loading = signal(false);
  loadError = signal<string | null>(null);

  searchText = signal('');
  historicalMode = signal(false);
  activeId = signal<number | null>(null);
  // expansion state per country (start with all collapsed)
  expandedCountries = signal<Set<string>>(new Set());
  // when true behaves like accordion (only one open at a time)
  accordionMode = signal(false);
  // list of BCR status descriptions to pass to toolbar
  bcrStatusDtos: BcrStatusDto[] = [];
  bcrStatuses: string[] = [];
  selectedStatusDescription: string | null = null;

  get showStatusDropdown() {
    return this.userPermissions.hasStatusDropdownPermission();
  }

  ngOnInit() {
    this.fetchGroups();
    this.selectedLegalEntity.setName('');
    this.selectedLegalEntity.setStatus('');
    this.loadBcrStatuses();
  }

  private loadBcrStatuses() {
    this.service.getBcrStatuses().subscribe({
      next: (arr) => {
        this.bcrStatusDtos = arr;
        this.bcrStatuses = arr.map((s) => s.status_name);
        this.selectedStatusDescription = null;
      },
      error: () => {
        this.bcrStatusDtos = [];
        this.bcrStatuses = [];
        this.selectedStatusDescription = null;
      },
    });
  }

  private fetchGroups() {
    this.loading.set(true);
    this.loadError.set(null);
    this.service.getLegalEntities().subscribe({
      next: (groups) => {
        this.countryGroups = groups;
        // keep all collapsed on initial load
        this.loading.set(false);

        // Check if there's an entityId query parameter to auto-select
        this.route.queryParams.subscribe((params) => {
          const entityIdParam = params['entityId'];
          if (entityIdParam) {
            const entityId = parseInt(entityIdParam, 10);
            if (!isNaN(entityId)) {
              this.autoSelectEntity(entityId);
            }
          }
        });
      },
      error: () => {
        this.loadError.set('Failed to load legal entities');
        this.loading.set(false);
      },
    });
  }

  displayGroups(): CountryGroup[] {
    const q = this.searchText().toLowerCase().trim();
    if (!q) return this.countryGroups;
    return this.countryGroups
      .map((g) => ({
        country: g.country,
        legal_entities: g.legal_entities.filter((e) => e.name.toLowerCase().includes(q)),
      }))
      .filter((g) => g.legal_entities.length);
  }

  select(entityId: number) {
    this.activeId.set(entityId);
    // find entity name to publish to shared service
    const entity = this.countryGroups
      .flatMap((g) => g.legal_entities)
      .find((e) => e.id === entityId);
    if (entity) {
      this.selectedLegalEntity.setName(entity.name);
      this.selectedLegalEntity.setId(entityId);
      this.selectedLegalEntity.setStatus('');
      this.selectedLegalEntity.setBcrStatusId(null);
      this.selectedLegalEntity.setStatusLoading(true);
      this.selectedLegalEntity.setEntityDetail(null);
    }
  }

  /**
   * Auto-selects a legal entity by ID and expands its country accordion.
   * Used when navigating from a notification with entityId query parameter.
   */
  private autoSelectEntity(entityId: number) {
    // Find the entity and its country
    for (const group of this.countryGroups) {
      const entity = group.legal_entities.find((e) => e.id === entityId);
      if (entity) {
        // Expand the country accordion
        const current = new Set(this.expandedCountries());
        current.add(group.country);
        this.expandedCountries.set(current);

        // Select the entity
        this.select(entityId);

        // Scroll to the entity after a small delay to ensure DOM is ready
        setTimeout(() => {
          const element = document.querySelector(`[data-entity-id="${entityId}"]`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);

        // Clear the query parameter from URL to keep it clean
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: {},
          replaceUrl: true,
        });

        break;
      }
    }
  }

  isExpanded(country: string): boolean {
    return this.expandedCountries().has(country);
  }

  toggleCountry(country: string) {
    const current = new Set(this.expandedCountries());
    if (current.has(country)) {
      current.delete(country);
    } else {
      if (this.accordionMode()) {
        current.clear();
      }
      current.add(country);
    }
    this.expandedCountries.set(current);
  }

  onSearchChange(value: string) {
    this.searchText.set(value);
  }

  onHistoricalChange(event: Event) {
    const input = event.target as HTMLInputElement | null;
    if (input) {
      this.historicalMode.set(input.checked);
    }
  }

  trackByCountry(index: number, group: CountryGroup) {
    return group.country;
  }

  constructor(
    private service: LegalEntityService,
    private selectedLegalEntity: SelectedLegalEntityService,
    private userPermissions: UserPermissionsService,
    private route: ActivatedRoute,
    private router: Router,
  ) {}
}
